import { useState, useEffect } from "react";
import { changeStaffRole, inviteStaff, removeStaff } from "../../../../lib/api/manager.api";
import type { Restaurant } from "../../restaurant.types";
import { useAuth } from "../../../../Context/AuthContext";
import { User } from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────
type StaffRole = "Owner" | "Manager" | "Staff";
type StaffStatus = "invited" | "active" | "suspended" | "removed";

interface StaffMember {
  _id: string;
  user: {
    name: string
  };
  userId: string | null;
  role: Exclude<StaffRole, "Owner">;
  status: StaffStatus;
  inviteToken: string | null;
  inviteExpiresAt: string | null;
  invitedBy: string | null;
  invitedAt: string | null;
  acceptedAt: string | null;
  displayEmail?: string;
}


interface CurrentUser {
  uid: string;
  role: StaffRole;
  token: string | null;
}

function getRemainingTime(isoDate: string | null): string {
  if (!isoDate) return "";
  const diff = new Date(isoDate).getTime() - Date.now();
  if (diff <= 0) return "Expired";
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  return `${h}h ${m}m left`;
}

function canInvite(role: StaffRole, targetRole: Exclude<StaffRole, "Owner">) {
  if (role === "Owner") return true;
  if (role === "Manager" && targetRole === "Staff") return true;
  return false;
}

function canRemove(
  callerRole: StaffRole,
  targetRole: Exclude<StaffRole, "Owner">
) {
  if (callerRole === "Owner") return true;
  if (callerRole === "Manager" && targetRole === "Staff") return true;
  return false;
}

// ─── Role Badge ───────────────────────────────────────────────────────────────
function RoleBadge({ role }: { role: StaffRole }) {
  const styles: Record<StaffRole, string> = {
    Owner: "bg-amber-900/40 text-amber-300 border border-amber-600/30",
    Manager: "bg-blue-900/40 text-blue-300 border border-blue-600/30",
    Staff: "bg-green-900/40 text-green-300 border border-green-600/30",
  };
  return (
    <span
      className={`text-[0.65rem] font-semibold uppercase tracking-widest px-2 py-0.5 rounded ${styles[role]}`}
    >
      {role}
    </span>
  );
}

// ─── Status Pill ──────────────────────────────────────────────────────────────
function StatusPill({
  status,
  expiresAt,
}: {
  status: StaffStatus;
  expiresAt?: string | null;
}) {
  if (status === "invited") {
    const t = getRemainingTime(expiresAt ?? null);
    const expired = t === "Expired";
    return (
      <span
        className={`text-[0.65rem] font-medium px-2 py-0.5 rounded-full border ${
          expired
            ? "bg-red-900/30 text-red-400 border-red-800/40"
            : "bg-amber-900/30 text-amber-400 border-amber-700/40"
        }`}
      >
        {expired ? "Expired" : `Pending · ${t}`}
      </span>
    );
  }
  const styles: Record<StaffStatus, string> = {
    active: "bg-green-900/30 text-green-400 border-green-800/40",
    invited: "bg-amber-900/30 text-amber-400 border-amber-700/40",
    suspended: "bg-red-900/30 text-red-400 border-red-800/40",
    removed: "bg-zinc-800/60 text-zinc-500 border-zinc-700/40",
  };
  return (
    <span
      className={`text-[0.65rem] font-medium px-2 py-0.5 rounded-full border capitalize ${styles[status]}`}
    >
      {status}
    </span>
  );
}

// ─── Spinner ──────────────────────────────────────────────────────────────────
function Spinner({ size = "sm" }: { size?: "sm" | "md" | "lg" }) {
  const s = { sm: "w-3 h-3", md: "w-4 h-4", lg: "w-8 h-8 border-2" }[size];
  return (
    <span
      className={`inline-block ${s} rounded-full border border-transparent border-t-current animate-spin`}
    />
  );
}

// ─── Invite Modal ─────────────────────────────────────────────────────────────
function InviteModal({
  restaurantId,
  callerRole,
  token,
  onClose,
  onInvited,
}: {
  restaurantId: string;
  callerRole: StaffRole;
  token: string;
  onClose: () => void;
  onInvited: (role: string, message?: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"Manager" | "Staff">(
    callerRole === "Manager" ? "Staff" : "Manager"
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const availableRoles: Array<"Manager" | "Staff"> =
    callerRole === "Owner" ? ["Manager", "Staff"] : ["Staff"];

  async function submit() {
    if (!email.trim()) return setError("Email is required.");
    setLoading(true);
    setError("");
    try {
      const data = await inviteStaff(restaurantId, email, role, token);
      onInvited(role, data?.message);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-stone-900 border border-stone-700/60 rounded-2xl shadow-2xl animate-[slideUp_0.2s_ease]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-800">
          <h2 className="text-lg font-semibold text-stone-100">
            Invite Team Member
          </h2>
          <button
            onClick={onClose}
            className="text-stone-500 hover:text-stone-300 hover:bg-stone-800 rounded-lg p-1.5 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          <div>
            <label className="block text-[0.7rem] font-semibold uppercase tracking-widest text-stone-500 mb-1.5">
              Email Address
            </label>
            <input
              type="email"
              placeholder="colleague@restaurant.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              className="w-full bg-stone-950 border border-stone-700 rounded-xl text-stone-200 text-sm px-4 py-2.5 outline-none focus:border-amber-500 placeholder:text-stone-600 transition-colors"
            />
          </div>

          <div>
            <label className="block text-[0.7rem] font-semibold uppercase tracking-widest text-stone-500 mb-1.5">
              Role
            </label>
            <div className="flex gap-2">
              {availableRoles.map((r) => (
                <button
                  key={r}
                  onClick={() => setRole(r)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                    role === r
                      ? "bg-amber-900/40 border-amber-500 text-amber-300"
                      : "bg-stone-950 border-stone-700 text-stone-500 hover:border-stone-600 hover:text-stone-400"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
            <p className="text-xs text-stone-600 mt-2 leading-relaxed">
              {role === "Manager"
                ? "Managers can invite Staff and manage daily operations."
                : "Staff members can view and handle daily operations."}
            </p>
          </div>

          {error && (
            <p className="text-xs text-red-400 bg-red-900/20 border border-red-800/40 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-stone-800">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-stone-400 border border-stone-700 rounded-xl hover:bg-stone-800 hover:text-stone-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={loading}
            className="px-5 py-2 text-sm font-semibold bg-amber-500 text-stone-950 rounded-xl hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {loading && <Spinner size="sm" />}
            Send Invite
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Invite Link Modal ────────────────────────────────────────────────────────
function InviteSentModal({
  role,
  message,
  onClose,
}: {
  role: string;
  message?: string;
  onClose: () => void;
}) {

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-stone-900 border border-stone-700/60 rounded-2xl shadow-2xl animate-[slideUp_0.2s_ease]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-800">
          <h2 className="text-lg font-semibold text-stone-100 flex items-center gap-2">
            <span className="text-green-400">✓</span> Invite Sent
          </h2>
          <button
            onClick={onClose}
            className="text-stone-500 hover:text-stone-300 hover:bg-stone-800 rounded-lg p-1.5 transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-stone-400 leading-relaxed">
            {message || (
              <>
                Invitation email sent to your new{" "}
                <span className="text-amber-400 font-medium">{role}</span>.
              </>
            )}
          </p>
        </div>

        <div className="flex justify-end px-6 py-4 border-t border-stone-800">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-semibold bg-amber-500 text-stone-950 rounded-xl hover:bg-amber-400 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Action Dropdown ──────────────────────────────────────────────────────────
function ActionMenu({
  member,
  callerRole,
  restaurantId,
  token,
  onRemoved,
  onRoleChanged,
}: {
  member: StaffMember;
  callerRole: StaffRole;
  restaurantId: string;
  token: string;
  onRemoved: (id: string) => void;
  onRoleChanged: (id: string, role: "Manager" | "Staff") => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const showRemove =
    canRemove(callerRole, member.role) && member.status !== "removed";
  const showRoleChange =
    callerRole === "Owner" && member.status === "active";

  if (!showRemove && !showRoleChange) return null;

  async function remove() {
    if (!confirm("Remove this staff member?")) return;
    setBusy(true);
    try {
      const res = await removeStaff(restaurantId, member._id, token);
      if (res.message) onRemoved(member._id);
    } finally {
      setBusy(false);
      setOpen(false);
    }
  }

  async function changeRole(newRole: "Manager" | "Staff") {
    if (newRole === member.role) return setOpen(false);
    setBusy(true);
    try {
      const res = await changeStaffRole(restaurantId, member._id, newRole, token);
      if (res.message) onRoleChanged(member._id, newRole);
    } finally {
      setBusy(false);
      setOpen(false);
    }
  }

  return (
    <div className="relative shrink-0">
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        className="text-stone-500 hover:text-stone-300 hover:bg-stone-700/60 rounded-lg px-2 py-1.5 text-lg leading-none transition-colors disabled:opacity-40"
      >
        {busy ? <Spinner size="sm" /> : "⋯"}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1.5 z-20 min-w-[160px] bg-stone-900 border border-stone-700/60 rounded-xl shadow-2xl py-1.5 overflow-hidden">
            {showRoleChange && (
              <>
                <p className="text-[0.6rem] uppercase tracking-widest text-stone-600 px-3 pt-1 pb-1">
                  Change Role
                </p>
                {(["Manager", "Staff"] as const)
                  .filter((r) => r !== member.role)
                  .map((r) => (
                    <button
                      key={r}
                      onClick={() => changeRole(r)}
                      className="w-full text-left text-sm text-stone-300 hover:bg-stone-800 px-3 py-2 transition-colors"
                    >
                      Make {r}
                    </button>
                  ))}
                {showRemove && (
                  <div className="border-t border-stone-800 my-1" />
                )}
              </>
            )}
            {showRemove && (
              <button
                onClick={remove}
                className="w-full text-left text-sm text-red-400 hover:bg-red-900/20 px-3 py-2 transition-colors"
              >
                Remove Member
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Staff Row ────────────────────────────────────────────────────────────────
function StaffRow({
  member,
  callerRole,
  restaurantId,
  token,
  onRemoved,
  onRoleChanged,
}: {
  member: StaffMember;
  callerRole: StaffRole;
  restaurantId: string;
  token: string;
  onRemoved: (id: string) => void;
  onRoleChanged: (id: string, role: "Manager" | "Staff") => void;
}) {

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 bg-stone-900 rounded-xl border transition-colors ${
        member.status === "invited"
          ? "border-dashed border-stone-700/50"
          : "border-stone-800/80 hover:border-stone-700"
      }`}
    >
      <div className="w-9 h-9 shrink-0 rounded-full bg-stone-800 border border-stone-700 flex items-center justify-center text-xs font-semibold text-amber-400">
        {member?.user?.name.slice(0, 2).toUpperCase()}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm text-stone-200 font-medium truncate">
          {member?.user?.name}
        </p>
        <div className="flex items-center gap-1.5 flex-wrap mt-1">
          <RoleBadge role={member.role} />
          <StatusPill
            status={member.status}
            expiresAt={member.inviteExpiresAt}
          />
        </div>
      </div>

      {member.status === "active" && member.acceptedAt && (
        <span className="hidden sm:block text-xs text-stone-600 shrink-0">
          Joined{" "}
          {new Date(member.acceptedAt).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </span>
      )}
      {member.status === "invited" && member.invitedAt && (
        <span className="hidden sm:block text-xs text-stone-600 shrink-0">
          Invited{" "}
          {new Date(member.invitedAt).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })}
        </span>
      )}

      <ActionMenu
        member={member}
        callerRole={callerRole}
        restaurantId={restaurantId}
        token={token}
        onRemoved={onRemoved}
        onRoleChanged={onRoleChanged}
      />
    </div>
  );
}

// ─── Owner Row ────────────────────────────────────────────────────────────────
function OwnerRow({ ownerId }: { ownerId: string }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-amber-900/10 rounded-xl border border-amber-700/20">
      <div className="w-9 h-9 shrink-0 rounded-full bg-amber-900/40 border border-amber-600/40 flex items-center justify-center text-xs font-semibold text-amber-300">
        <User size={15}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          <RoleBadge role="Owner" />
          <StatusPill status="active" />
        </div>
      </div>
    </div>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ num, label }: { num: number; label: string }) {
  return (
    <div className="flex flex-col items-center gap-0.5 bg-stone-100 border border-amber-600/20 rounded-xl py-4 px-2">
      <span className="text-3xl font-bold text-amber-400">{num}</span>
      <span className="text-[0.65rem] uppercase tracking-widest text-stone-600 text-center leading-tight">
        {label}
      </span>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
interface StaffManagementProps {
  restaurant: Restaurant | null;
}

export function StaffManagement({ restaurant }: StaffManagementProps) {
  const { user, accessToken } = useAuth();
  const currentUser: CurrentUser = {
    uid: user?.id ?? "",
    role: "Staff",
    token: accessToken,
  };

  const [staff, setStaff] = useState<StaffMember[]>(restaurant?.staff ?? []);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteResult, setInviteResult] = useState<{
    role: string;
    message?: string;
  } | null>(null);

  useEffect(() => {
    setStaff(restaurant?.staff ?? []);
}, [restaurant?._id, restaurant?.staff]);

const callerRole: StaffRole = (() => {
    if (!restaurant) return "Staff";
    if (restaurant.ownerId === currentUser.uid) return "Owner";
    const s = staff.find((m) => m.userId === currentUser.uid && m.status === "active");
    return (s?.role as StaffRole) ?? "Staff";
})();

  function handleRemoved(staffId: string) {
    setStaff((prev) => prev.filter((s) => s._id !== staffId));
  }

  function handleRoleChanged(staffId: string, role: "Manager" | "Staff") {
    setStaff((prev) => prev.map((s) => (s._id === staffId ? { ...s, role } : s)));
  }

  const activeStaff = staff.filter((s) => s.status === "active");
  const invitedStaff = staff.filter((s) => s.status === "invited");
  const nonRemovedCount = staff.filter((s) => s.status !== "removed").length;
  const maxReached = nonRemovedCount >= 3;
  const canInviteAny = Boolean(currentUser.token) && canInvite(callerRole, "Staff");

  return (
    <>
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(10px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>

      <div className="">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-3xl font-bold tracking-tight">
              Team Management
            </h1>
            <p className="text-sm text-stone-500 mt-0.5">
              Manage your restaurant staff and permissions
            </p>
          </div>
          {restaurant && canInviteAny && (
            <button
              onClick={() => setShowInvite(true)}
              disabled={maxReached}
              title={maxReached ? "Staff limit reached (3 members)" : ""}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-amber-400 text-stone-950 rounded-xl hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              + Add Staff
            </button>
          )}
        </div>

        {!restaurant ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <p className="text-sm text-stone-600">No restaurant found.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {(callerRole === "Owner" || callerRole === "Manager") && <div className="grid grid-cols-3 gap-3">
              <StatCard num={activeStaff.length + 1} label="Active Members" />
              <StatCard num={invitedStaff.length} label="Pending Invites" />
              <StatCard num={Math.max(0, 3 - nonRemovedCount)} label="Slots Free" />
            </div>}

            <section>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-[0.7rem] font-semibold uppercase tracking-widest text-stone-500">
                  Active Members
                </h3>
                <span className="text-[0.65rem] px-2 py-0.5 rounded-full bg-stone-800 border border-stone-700 text-amber-400">
                  {activeStaff.length + 1}
                </span>
              </div>
              <div className="space-y-2">
                {callerRole === "Owner" && <OwnerRow ownerId={restaurant.ownerId} />}
                {activeStaff.map((m) => (
                  <StaffRow
                    key={m._id}
                    member={m}
                    callerRole={callerRole}
                    restaurantId={restaurant._id}
                    token={currentUser.token ?? ""}
                    onRemoved={handleRemoved}
                    onRoleChanged={handleRoleChanged}
                  />
                ))}
                {activeStaff.length === 0 && (
                  <p className="text-sm text-stone-600 italic px-4 py-3">
                    No additional active members yet.
                  </p>
                )}
              </div>
            </section>

            {invitedStaff.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-[0.7rem] font-semibold uppercase tracking-widest text-stone-500">
                    Pending Invitations
                  </h3>
                  <span className="text-[0.65rem] px-2 py-0.5 rounded-full bg-stone-800 border border-stone-700 text-amber-400">
                    {invitedStaff.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {invitedStaff.map((m) => (
                    <StaffRow
                      key={m._id}
                      member={m}
                      callerRole={callerRole}
                      restaurantId={restaurant._id}
                      token={currentUser.token ?? ""}
                      onRemoved={handleRemoved}
                      onRoleChanged={handleRoleChanged}
                    />
                  ))}
                </div>
              </section>
            )}

            <div className="flex gap-3 items-start bg-stone-900 border-l-2 border-amber-500 border border-stone-800 rounded-r-xl px-4 py-3 text-xs text-stone-500 leading-relaxed">
              <span className="text-amber-500 mt-0.5 shrink-0">i</span>
              <span>
                {callerRole === "Owner"
                  ? "As Owner, you can invite Managers & Staff, change roles, and remove any member."
                  : callerRole === "Manager"
                  ? "As Manager, you can invite and remove Staff members."
                  : "Staff members do not have team management access."}
              </span>
            </div>
          </div>
        )}
      </div>

      {showInvite && restaurant && currentUser.token && (
        <InviteModal
          restaurantId={restaurant._id}
          callerRole={callerRole}
          token={currentUser.token}
          onClose={() => setShowInvite(false)}
          onInvited={(role, message) => {
            setShowInvite(false);
            setInviteResult({ role, message });
          }}
        />
      )}
      {inviteResult && (
        <InviteSentModal
          role={inviteResult.role}
          message={inviteResult.message}
          onClose={() => setInviteResult(null)}
        />
      )}
    </>
  );
}
